import { useState } from 'react';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import GroupIcon from '@mui/icons-material/Group';
import { useTranslation } from 'react-i18next';
import { GarageCarRow } from './GarageCarRow';
import { urgencyRank } from './goals/goalProgress';
import type { Garage } from '../api/types';

interface Props {
  garage: Garage;
  /** Owners get the actions menu and edit rights; followers read only. */
  isMine: boolean;
  onRename: () => void;
  onManageFollowers: () => void;
  onDelete: () => void;
}

/** DOM id of a garage's block — the sidebar scrolls to it. */
export const garageAnchorId = (garageId: number): string => `garage-${garageId}`;

/**
 * One garage on the garage page: every car as the same row card, cars with
 * the most important open goal first.
 */
export function GarageSection({ garage, isMine, onRename, onManageFollowers, onDelete }: Props) {
  const { t } = useTranslation();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const cars = garage.cars ?? [];

  const closeMenuAnd = (action: () => void) => () => {
    setMenuAnchor(null);
    action();
  };

  return (
    <Box
      component="section"
      id={garageAnchorId(garage.id)}
      aria-label={garage.name}
      sx={{ scrollMarginTop: 16 }}
    >
      <Stack spacing={2}>
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="h5">{garage.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {isMine
                ? t('garage.carCount', { count: cars.length })
                : t('garage.ownedBy', { name: garage.user?.name ?? '' })}
            </Typography>
          </Box>
          {isMine && (
            <IconButton
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              aria-label={t('garage.actions.more')}
            >
              <MoreVertIcon />
            </IconButton>
          )}
        </Stack>

        {cars.length === 0 && (
          <Typography color="text.secondary">
            {isMine ? t('garage.noCarsYet') : t('garage.emptyGarage')}
          </Typography>
        )}

        <Stack spacing={2}>
          {[...cars]
            .sort((a, b) => urgencyRank(a.goals) - urgencyRank(b.goals))
            .map((car) => (
              <GarageCarRow key={car.id} car={car} canManage={isMine} />
            ))}
        </Stack>
      </Stack>

      <Menu anchorEl={menuAnchor} open={menuAnchor !== null} onClose={() => setMenuAnchor(null)}>
        <MenuItem onClick={closeMenuAnd(onRename)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('garage.actions.rename')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={closeMenuAnd(onManageFollowers)}>
          <ListItemIcon>
            <GroupIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            {t('garage.actions.followersCount', { count: garage.follower_count ?? 0 })}
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={closeMenuAnd(onDelete)}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('garage.actions.delete')}</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
}
